import { useContext } from "react";
import { buildClasses } from "../utils/buildClasses";
import { TransactionColumn } from "./TransactionColumn";
import { TransactionsAmountWidthContext } from "./transactionsAmountWidth";

// Primary responsibility is keeping the width of the column correct for
// column headers and values.
export function TransactionColumnSizer({
  column,
  children,
}: {
  column: TransactionColumn;
  children?: JSX.Element;
}): JSX.Element {
  // The Amount column fits its longest amount when that's known.
  const amountWidth = useContext(TransactionsAmountWidthContext);
  const fittedAmount = column === TransactionColumn.AMOUNT && amountWidth;

  return (
    <div
      className={buildClasses(
        fittedAmount
          ? "flex-none"
          : {
              switch: column,
              cases: new Map([
                [
                  TransactionColumn.DATE,
                  // Fits the widest date we show, "May 30, 2044", and the
                  // header, with the cell's padding.
                  buildClasses("w-[7.5rem]", "flex-grow-0", "flex-shrink-0"),
                ],
                [
                  TransactionColumn.ACCOUNT,
                  buildClasses("w-36", "flex-grow-[0.4]", "flex-shrink-[0.4]"),
                ],
                [
                  TransactionColumn.TYPE,
                  buildClasses("w-32", "flex-grow-[0.4]", "flex-shrink-[0.4]"),
                ],
                [
                  TransactionColumn.MERCHANT,
                  buildClasses("w-32", "flex-grow-[0.4]", "flex-shrink-[0.4]"),
                ],
                [
                  TransactionColumn.DESCRIPTION,
                  buildClasses("w-36", "flex-grow-[0.6]", "flex-shrink-[0.6]"),
                ],
                [
                  TransactionColumn.CATEGORY,
                  buildClasses("w-36", "flex-grow-[0.4]", "flex-shrink-[0.4]"),
                ],
                [
                  TransactionColumn.TAGS,
                  buildClasses("w-24", "flex-grow-[0.2]", "flex-shrink-[0.4]"),
                ],
                [
                  TransactionColumn.RULE,
                  buildClasses("w-8", "flex-grow-0", "flex-shrink-0"),
                ],
                [
                  TransactionColumn.REVIEWED,
                  buildClasses("w-8", "flex-grow-0", "flex-shrink-0"),
                ],
                [
                  TransactionColumn.AMOUNT,
                  buildClasses("w-36", "flex-grow-[0.1]", "flex-shrink-[0.1]"),
                ],
              ]),
            },
        // Lets columns shrink below their contents, which truncate, so the
        // table always fits its width.
        "min-w-0",
        "flex",
        "items-center",
        "h-full",
      )}
      style={fittedAmount ? { width: amountWidth } : undefined}
    >
      {children}
    </div>
  );
}

export const TRANSACTION_CELL_PADDING_X = "px-3";
